import { Alert, Button, Modal, Text } from '@ui/themed';
import { t } from 'i18next';
import { useState } from 'react';
import { useHandlers } from '../../../context/page-context';
import { useModal } from '../../../hooks/use-modal';
import type { I18nPrefix } from '../../../i18n/prefix';
import { useStorefrontOptionsControls } from '../context/storefront-options-context';
import { importSparkOptionsSnippet } from '../schemas';
import { serializeSparkOptions } from '../utils/snippet';

const CARD =
  'features:storefront_options.pages.storefront_options_page.import_export' satisfies I18nPrefix;

export function ImportExportCard() {
  const { options, setOptions } = useStorefrontOptionsControls();
  const { notificationHandler } = useHandlers();
  const { isOpen, openModal, closeModal } = useModal();

  const [snippet, setSnippet] = useState<string>('');
  const [error, setError] = useState<string | null>(null);
  const [warnings, setWarnings] = useState<string[]>([]);

  async function handleCopy() {
    try {
      await navigator.clipboard.writeText(serializeSparkOptions(options));
      notificationHandler.notify(t('notifications:copied'), undefined, 'success');
    } catch {
      notificationHandler.notify(t(`${CARD}.copy_error`), undefined, 'error');
    }
  }

  function openImport() {
    setSnippet('');
    setError(null);
    openModal();
  }

  function handleImport() {
    let result: ReturnType<typeof importSparkOptionsSnippet>;
    try {
      // Resolve against the current draft outside the state updater, so warnings are collected once.
      result = importSparkOptionsSnippet(options, snippet);
    } catch {
      setError(t(`${CARD}.import_error`));
      return;
    }
    setOptions(() => result.options);
    setWarnings(result.warnings);
    setError(null);
    closeModal();
    notificationHandler.notify(t(`${CARD}.import_success`), undefined, 'success');
  }

  return (
    <>
      <Alert variant="info" title={t(`${CARD}.title`)} headingLevel="h2">
        <div className="flex w-full flex-col gap-2">
          <span>{t(`${CARD}.subtitle`)}</span>
          <div className="flex flex-wrap gap-2">
            <Button variant="secondary" icon="upload" onClick={openImport}>
              {t(`${CARD}.import`)}
            </Button>
            <Button variant="secondary" icon="copy" onClick={() => void handleCopy()}>
              {t(`${CARD}.copy`)}
            </Button>
          </div>
        </div>
      </Alert>

      {warnings.length > 0 && (
        <Alert
          variant="warning"
          title={t(`${CARD}.warnings.title`)}
          headingLevel="h2"
          onDismiss={() => setWarnings([])}
        >
          <div className="flex w-full flex-col gap-2">
            <span>{t(`${CARD}.warnings.subtitle`)}</span>
            <ul className="list-disc ps-5 font-mono text-xs">
              {warnings.map((path) => (
                <li key={path}>{path}</li>
              ))}
            </ul>
          </div>
        </Alert>
      )}

      <Modal
        isOpen={isOpen}
        onClose={closeModal}
        primaryAction={{
          text: t(`${CARD}.modal.confirm`),
          onClick: handleImport,
          disabled: snippet.trim() === '',
        }}
        secondaryAction={{ text: t(`${CARD}.modal.cancel`), onClick: closeModal }}
      >
        <Modal.Header>{t(`${CARD}.modal.title`)}</Modal.Header>
        <Modal.Body>
          <div className="flex flex-col gap-2">
            <Text size="sm" variant="secondary">
              {t(`${CARD}.modal.description`)}
            </Text>
            <textarea
              value={snippet}
              rows={10}
              spellCheck={false}
              placeholder={t(`${CARD}.modal.placeholder`)}
              onChange={(event) => setSnippet(event.target.value)}
              className="w-full rounded-lg border border-gray-300 p-2 font-mono text-sm shadow-xs focus:border-platform-primary focus:ring-platform-primary"
            />
            {error && (
              <Text size="sm" variant="secondary">
                {error}
              </Text>
            )}
          </div>
        </Modal.Body>
      </Modal>
    </>
  );
}
